"use client";

import { FC } from "react";

/**
 * GROWTH:CODE クラシック装飾線
 *
 * 仕様: docs/spec/design_spec.md §3.3
 *   - 中央モチーフ + 両側水平線 + 端点ドット
 *   - 用途: テーマタイトル上下、カードセクション見出し
 *
 * 実装方針: 最終的には `asset/ornaments/divider_classic.svg` を img として
 * 表示する想定（asset 受領後に差し替え）。それまでの暫定として CSS のみで
 * 同等の構造（端点ドット + 両側水平線 + 中央菱形モチーフ）を再現する。
 */
interface DividerProps {
  /** 装飾線の色。未指定時は `--color-border-soft`。 */
  color?: string;
  /** 中央モチーフのサイズ (px)。デフォルト 8。 */
  motifSize?: number;
  /** 全体の最大幅 (px or %)。デフォルト '100%'。 */
  maxWidth?: number | string;
  className?: string;
}

const ClassicDivider: FC<DividerProps> = ({
  color,
  motifSize = 8,
  maxWidth = "100%",
  className,
}) => {
  const lineColor = color ?? "var(--color-border-soft, #d6d6d6)";
  const dotSize = 4;

  return (
    <div
      role="separator"
      aria-hidden="true"
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        maxWidth,
        margin: "0 auto",
        gap: 6,
      }}
    >
      {/* 左端ドット */}
      <span
        style={{
          width: dotSize,
          height: dotSize,
          borderRadius: "50%",
          backgroundColor: lineColor,
          flexShrink: 0,
        }}
      />
      {/* 左水平線 */}
      <span
        style={{
          flex: 1,
          height: 1,
          backgroundColor: lineColor,
        }}
      />
      {/* 中央菱形モチーフ */}
      <span
        style={{
          width: motifSize,
          height: motifSize,
          backgroundColor: lineColor,
          transform: "rotate(45deg)",
          flexShrink: 0,
        }}
      />
      {/* 右水平線 */}
      <span
        style={{
          flex: 1,
          height: 1,
          backgroundColor: lineColor,
        }}
      />
      {/* 右端ドット */}
      <span
        style={{
          width: dotSize,
          height: dotSize,
          borderRadius: "50%",
          backgroundColor: lineColor,
          flexShrink: 0,
        }}
      />
    </div>
  );
};

export default ClassicDivider;
